import {NotificationTypeType} from '@/config/EventTriggerConfig'
import {isSendEmailStep, isSendPushStep, isSendWhatsAppStep} from '@/helpers/rtnNotificationType'
import {CreateNotificationRequest, CreateNotificationRequestSchema} from '@/types/Notifications'
import {
  EmailStepType,
  isEmailCustomStep,
  isEmailTemplateStep,
  PushStepType,
  WhatsAppStepType,
} from '@/types/NotificationStep'
import {ParameterItem, Parameters} from '@/types/Traits'

const DEFAULT_LANGUAGE = {id_language: 1, name_language: 'English'}

export type CreateNotificationRequestBodyParams = {
  countryId: number
  eventStatus: number
  notificationName: string
  notificationDescription: string
  eventSelectors: ParameterItem
  paramSelectors: Parameters
  notificationType?: NotificationTypeType
  pushStep?: PushStepType
  whatsAppStep?: WhatsAppStepType
  emailStep?: EmailStepType
}

export const createNotificationRequestBody = ({
  countryId,
  eventStatus,
  notificationName,
  notificationDescription,
  eventSelectors,
  paramSelectors,
  notificationType,
  pushStep,
  whatsAppStep,
  emailStep,
}: CreateNotificationRequestBodyParams): CreateNotificationRequest => {
  const event = {
    id_trait: eventSelectors.trait.id,
    id_operator: eventSelectors.operator.id,
    id_traitvalue: eventSelectors.predefinedValue?.id ?? -1,
    value: eventSelectors.predefinedValue?.name ?? null,
  }

  const parameters = paramSelectors.map(parameterItem => {
    const updatedItem = {
      id_traitparameter: parameterItem.trait?.id,
      id_operator: parameterItem.operator?.id,
      id_traitparametervalue: parameterItem?.predefinedValue?.id ?? null,
      id_operatorvalue: parameterItem?.predefinedValue?.id ?? null, // [Task 13374760] should be id_traitparametervalue
      value: parameterItem.value ?? null,
    }
    return updatedItem
  })

  const isEmailCustomFlow = isSendEmailStep(notificationType) && !!emailStep && isEmailCustomStep(emailStep)
  const isEmailTemplateFlow = isSendEmailStep(notificationType) && !!emailStep && isEmailTemplateStep(emailStep)

  const buildMessageConfig = () => {
    if (isSendPushStep(notificationType)) {
      return [
        {
          ...DEFAULT_LANGUAGE,
          subject: pushStep?.subject ?? '',
          message: pushStep?.message ?? '',
          image: pushStep?.selectedImage ?? null,
          landing_page: pushStep?.selectedPage ?? null,
        },
      ]
    }

    if (isEmailCustomFlow) {
      return [
        {
          ...DEFAULT_LANGUAGE,
          subject: emailStep.emailSubject ?? '',
          message: emailStep.emailMessage ?? '',
          image: emailStep.selectedImage ?? null,
          landing_page: emailStep.selectedPage ?? null,
        },
      ]
    }

    return undefined
  }

  const buildWhatsAppMessageConfig = () => {
    if (isSendWhatsAppStep(notificationType)) {
      return [
        {
          ...DEFAULT_LANGUAGE,
          id_messagetemplate: whatsAppStep?.selectedTemplateId ?? '',
          message: whatsAppStep?.whatsAppMessage ?? '',
        },
      ]
    }

    if (isEmailTemplateFlow) {
      return [
        {
          ...DEFAULT_LANGUAGE,
          id_messagetemplate: emailStep.selectedTemplateId ?? '',
          subject: emailStep.emailSubject ?? '',
          message: emailStep.html ?? '',
        },
      ]
    }

    return undefined
  }

  const messageConfig = buildMessageConfig()
  const whatsAppMessageConfig = buildWhatsAppMessageConfig()

  const notificationData = {
    id_country: countryId,
    id_eventstatus: eventStatus,
    name: notificationName,
    description: notificationDescription,
    active: true,
    entry_conditions: [
      {
        ...event,
        traitparameters: parameters,
        is_primary: true,
      },
    ],
    steps: [
      {
        id_step: 1,
        id_steptype: Number(notificationType?.id ?? 1),
        id_stepstatus: 1,
        message_configs: messageConfig,
        message_templates: whatsAppMessageConfig,
      },
    ],
  }

  const validatedData = CreateNotificationRequestSchema.parse(notificationData)

  return validatedData
}
